import {useEffect, useState} from 'react';
import {Button, Input, Radio, Typography, Alert} from 'antd';
import DBModal from 'components/modal/modal';
import styles from '../kybDashboard.module.scss';

const {Text} = Typography;

/**
 * Accept or reject one uploaded file.
 *
 * `reviewerNote` is required server-side on a rejection, so the form blocks
 * before the request goes out — a reviewer should not learn that from a toast
 * after losing what they typed. The note is optional on accept and still
 * offered, since "accepted, but the address is out of date" is worth keeping.
 *
 * A ruling is effectively final: once a document is accepted or rejected the
 * business can no longer delete it and there is no undo operation, so the
 * modal says so rather than letting the reviewer find out afterwards.
 *
 * The KYC Hub reuses it for individuals, overriding the business-facing copy
 * through `noteLabel` and `notice`.
 */
export const ReviewDocumentModal = ({
  open,
  document,
  onClose,
  onSubmit,
  submitting,
  noteLabel = 'Note to the business',
  notice = 'This ruling cannot be undone, and it does not notify the business on its own. Set the account to Request more info or Reject once you are done with the files.',
}) => {
  const [status, setStatus] = useState('ACCEPTED');
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus('ACCEPTED');
      setNote('');
      setTouched(false);
    }
  }, [open, document?.id]);

  const noteMissing = status === 'REJECTED' && !note.trim();

  const handleSubmit = () => {
    setTouched(true);
    if (noteMissing) return;
    onSubmit({
      documentId: document?.id,
      status,
      ...(note.trim() && {reviewerNote: note.trim()}),
    });
  };

  return (
    <DBModal
      isOpen={open}
      handleClose={onClose}
      width={520}
      destroyOnClose
      content={
        <div className={styles.modalBody}>
          <h2 className={styles.modalTitle}>Review document</h2>
          <Text type="secondary">
            {document?.freeName || document?.fileName || 'Uploaded file'}
          </Text>

          <Radio.Group
            className={styles.modalField}
            value={status}
            onChange={event => setStatus(event.target.value)}>
            <Radio value="ACCEPTED">Accept</Radio>
            <Radio value="REJECTED">Reject</Radio>
          </Radio.Group>

          <div className={styles.modalField}>
            <label className={styles.fieldLabel}>
              {noteLabel}
              {status === 'REJECTED' ? ' (required)' : ' (optional)'}
            </label>
            <Input.TextArea
              rows={4}
              value={note}
              maxLength={500}
              showCount
              placeholder={
                status === 'REJECTED'
                  ? 'Say exactly what is wrong and what to upload instead.'
                  : 'Anything worth recording alongside this file.'
              }
              onChange={event => setNote(event.target.value)}
            />
            {touched && noteMissing && (
              <Text type="danger" className={styles.fieldError}>
                A note is required when rejecting a document.
              </Text>
            )}
          </div>

          <Alert
            type="info"
            showIcon
            className={styles.modalField}
            message={notice}
          />

          <div className={styles.modalActions}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              loading={submitting}
              danger={status === 'REJECTED'}
              onClick={handleSubmit}>
              {status === 'REJECTED' ? 'Reject document' : 'Accept document'}
            </Button>
          </div>
        </div>
      }
    />
  );
};
